import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useShallow } from "zustand/react/shallow";
import { CHAT_MAX_CHARS, isAiResidentActorId } from "@townsy/contracts";
import { useTownSession } from "../app/TownSessionContext";
import { flagLabel } from "../lib/flag";
import { formatSeconds } from "../lib/format";
import { selectIsGuest, useTownStore, type ChatEntry } from "../state/store";
import { FlagIcon } from "./FlagIcon";
import { ReportFlagIcon } from "./people/ReportFlagIcon";

/** ADR-0042 decision 1: the whole town is one street room, so the street chat has one name in every district. */
export const STREET_CHAT_NAME = "Townsy street chat";

/**
 * The chat panel's heading, region name and input placeholder: the street chat ("Townsy street chat · everyone on the
 * street") when `interiorName` is null, else the building's own chat ("Inside: Kite Co · room chat").
 */
export function chatRoomText(interiorName: string | null): { heading: string; detail: string; label: string; placeholder: string } {
  if (interiorName === null) {
    return { heading: STREET_CHAT_NAME, detail: "everyone on the street", label: `Chat: ${STREET_CHAT_NAME}`, placeholder: "Message everyone on the street" };
  }
  return { heading: `Inside: ${interiorName}`, detail: "room chat", label: `Chat: ${interiorName}`, placeholder: `Message ${interiorName}` };
}

const REJECTION_TEXT: Record<string, string> = {
  RATE_LIMITED: "You're sending messages too quickly.",
  DWELL_REQUIRED: "New visitors must wait a moment before chatting.",
  MUTED: "You are muted in this room.",
  INVALID: "That message can't be sent (empty or not allowed).",
  RESTRICTED: "Chat is restricted for this account.",
};

/** AI resident lines (ADR-0010): a soft cobalt card with a small "AI" badge, as legible as any line. */
const AI_LINE_STYLE: CSSProperties = {
  background: "var(--color-action-soft)",
  borderLeft: "3px solid var(--color-action)",
  borderRadius: "var(--radius-sm)",
  padding: "3px 8px",
};
const AI_BADGE_STYLE: CSSProperties = {
  display: "inline-block",
  marginLeft: 5,
  padding: "0 6px",
  borderRadius: "var(--radius-pill)",
  background: "var(--color-action)",
  color: "#fff",
  fontSize: "0.7rem",
  fontWeight: 800,
  lineHeight: "1.35",
  letterSpacing: "0.04em",
  verticalAlign: "1px",
};
const AI_BADGE_TITLE = "AI resident: a computer-run townsperson, not a real visitor";

/**
 * One chat line. AI residents (ADR-0010) are recognised by their server-issued actor id (no visitor
 * id can match): their name gets the "AI" badge instead of a flag and the line a soft cobalt card.
 * Text is always rendered as plain text.
 */
/** True when `text` addresses `label` with "@label" (the person card's Say hi), as a whole name, any case. */
export function mentions(text: string, label: string | null | undefined): boolean {
  if (!label) return false;
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^\\p{L}\\p{N}_])@${escaped}(?![\\p{L}\\p{N}_])`, "iu").test(text);
}

/**
 * One chat line. AI residents (ADR-0010) are recognised by their server-issued actor id (no visitor
 * id can match): their name gets the "AI" badge instead of a flag and the line a soft cobalt card.
 * A line addressed to you ("@YourName") is highlighted; a name opens that person's card (ADR-0013) while they are here.
 * Other people's lines (never yours, never system lines) have a "Report this message" flag: shown on hover and keyboard
 * focus, always on touch screens; it comes after the name in the line, so the name stays the line's first button.
 * Text is always rendered as plain text.
 */
export function ChatLine({
  message: m,
  mentionsMe = false,
  onOpenPerson,
  onReport,
}: {
  message: ChatEntry;
  mentionsMe?: boolean;
  onOpenPerson?: (actorId: string) => void;
  onReport?: (message: ChatEntry) => void;
}) {
  const ai = !m.system && isAiResidentActorId(m.actorId);
  const reportable = Boolean(onReport) && !m.own && !m.system;
  const name = (inner: ReactNode, title?: string) =>
    onOpenPerson && !m.own ? (
      <button type="button" className="who chat-who-btn" title={title ? `${title}. Open their card` : "Open their card"} onClick={() => onOpenPerson(m.actorId)}>
        {inner}
      </button>
    ) : (
      <span className="who" title={title}>
        {inner}
      </span>
    );
  return (
    <div
      className={`chat-msg${m.system ? " system" : ""}${ai ? " chat-msg-ai" : ""}${mentionsMe ? " chat-msg-mention" : ""}${reportable ? " chat-msg-reportable" : ""}`}
      style={ai ? AI_LINE_STYLE : undefined}
      data-ai-resident={ai ? "true" : undefined}
      data-mention={mentionsMe ? "true" : undefined}
      data-message-id={m.system ? undefined : m.id}
    >
      {m.system
        ? null
        : ai
          ? name(
              <>
                {m.label}
                <span style={AI_BADGE_STYLE} title={AI_BADGE_TITLE} aria-label="AI resident">
                  AI
                </span>
                :
              </>,
            )
          : name(
              <>
                <FlagIcon code={m.countryCode} /> {m.label}
                {m.own ? <span className="faint small"> (you)</span> : null}:
              </>,
              flagLabel(m.countryCode),
            )}{" "}
      <span>{m.text}</span>
      {reportable ? (
        <button
          type="button"
          className="chat-report-btn"
          aria-label={`Report this message from ${m.label}`}
          title="Report this message"
          onClick={() => onReport?.(m)}
          data-testid="chat-report"
        >
          <ReportFlagIcon />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Room chat (docs/03 section 7): plain text only, 280 chars, scoped to the current room by the
 * server. Collapsed by default; Escape closes. Typing here never moves the avatar (A09) because
 * the store flags `chatFocused` while the box has focus.
 */
export function ChatPanel() {
  const session = useTownSession();
  const { open, messages, rejection, joinedAt, dwellSeconds, minIntervalMs, lastSentAt, status, chatEnabled } = useTownStore(
    useShallow((s) => ({
      open: s.ui.chatOpen,
      messages: s.chat.messages,
      rejection: s.chat.rejection,
      joinedAt: s.room.joinedAt,
      dwellSeconds: s.room.chatDwellSeconds,
      minIntervalMs: s.room.chatMinIntervalMs,
      lastSentAt: s.chat.lastSentAt,
      status: s.room.status,
      chatEnabled: s.bootstrap?.features.guestChatEnabled ?? true,
    })),
  );
  const isGuest = useTownStore(selectIsGuest);
  const interiorName = useTownStore((s) => {
    if (!s.room.propertyId) return null;
    const p = s.properties[s.room.propertyId];
    return p?.placement?.businessName ?? p?.civic?.name ?? p?.address ?? "Inside";
  });
  const room = chatRoomText(interiorName);
  const toggleChat = useTownStore((s) => s.toggleChat);
  const setUi = useTownStore((s) => s.setUi);
  const mutes = useTownStore((s) => s.mutes);
  const actorsHere = useTownStore((s) => s.actors);
  const roomKey = useTownStore((s) => s.room.roomKey);
  const myLabel = useTownStore((s) => (s.localActorId ? s.actors[s.localActorId]?.label : undefined) ?? s.bootstrap?.actor.label ?? null);
  const draft = useTownStore((s) => s.chatDraft);
  const [text, setText] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const dwellUntil = isGuest && joinedAt ? joinedAt + dwellSeconds * 1000 : 0;
  const dwellLeft = Math.max(0, dwellUntil - now);
  const retryUntil = rejection?.retryAfterMs ? rejection.at + rejection.retryAfterMs : 0;
  const retryLeft = Math.max(0, retryUntil - now);
  const intervalLeft = Math.max(0, lastSentAt + minIntervalMs - now);

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (open) listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages.length, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // "Say hi" from a person card: take the draft once, keep anything already typed after it, caret at the end.
  useEffect(() => {
    if (!open || !draft) return;
    setText((cur) => (cur.trim() ? `${draft.text}${cur.trim()}` : draft.text));
    useTownStore.getState().setChatDraft(null);
    // ADR-0053: starting a line to someone counts as typing (an AI resident talking with you waits for it).
    session?.noteChatTyping();
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  }, [open, draft, session]);

  if (!open) return null;
  const hiddenCount = messages.filter((m) => !m.system && m.actorId in mutes).length;
  const joined = status === "joined";
  const guestBlocked = isGuest && !chatEnabled;
  const canSend = joined && !guestBlocked && dwellLeft === 0 && text.trim().length > 0 && text.length <= CHAT_MAX_CHARS;

  const send = () => {
    if (!session || !canSend) return;
    if (session.sendChat(text)) setText("");
  };

  return (
    <section className="chat" role="region" aria-label={room.label} data-movement-block="true">
      <div className="sheet-handle" aria-hidden="true" />
      <div className="panel-header" style={{ padding: "8px 12px" }}>
        <h2 style={{ fontSize: "0.95rem" }} data-testid="chat-title">
          {room.heading}
          <span className="faint small"> · {room.detail}</span>
        </h2>
        <button type="button" className="btn btn-ghost icon-btn" aria-label="Close chat" onClick={() => toggleChat(false)}>
          ×
        </button>
      </div>
      <div className="chat-messages" ref={listRef} role="log" aria-live="polite" aria-relevant="additions">
        {messages.length === 0 ? <div className="chat-msg system">No messages yet.</div> : null}
        {messages.map((m) =>
          !m.system && m.actorId in mutes ? null : (
            <ChatLine
              key={m.id}
              message={m}
              mentionsMe={!m.own && !m.system && mentions(m.text, myLabel)}
              onOpenPerson={!m.system && actorsHere[m.actorId] ? (id) => useTownStore.getState().selectActor(id) : undefined}
              onReport={(line) =>
                useTownStore.getState().openPersonReport({
                  actorId: line.actorId,
                  label: line.label,
                  ...(roomKey ? { roomKey } : {}),
                  message: { messageId: line.id, text: line.text, at: line.at },
                })
              }
            />
          ),
        )}
        {hiddenCount > 0 ? (
          <div className="chat-msg system" data-testid="chat-muted-hidden">
            {hiddenCount === 1 ? "1 message" : `${hiddenCount} messages`} from people you muted {hiddenCount === 1 ? "is" : "are"} hidden.
          </div>
        ) : null}
      </div>
      <form
        className="chat-form"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        {!joined ? <div className="small muted">Chat is available once you are in a room.</div> : null}
        {guestBlocked ? <div className="small muted">Guest chat is switched off in this environment. Sign in to chat.</div> : null}
        {joined && dwellLeft > 0 ? (
          <div className="small muted" role="status">
            New visitors can chat in {formatSeconds(dwellLeft)}.
          </div>
        ) : null}
        {rejection ? (
          <div className="small error-text" role="alert">
            {REJECTION_TEXT[rejection.code] ?? rejection.message}
            {retryLeft > 0 ? ` Try again in ${formatSeconds(retryLeft)}.` : ""}
          </div>
        ) : intervalLeft > 0 && !rejection ? (
          <div className="small faint">Next message in {formatSeconds(intervalLeft)}</div>
        ) : null}
        <div className="row">
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            maxLength={CHAT_MAX_CHARS + 20}
            placeholder={joined ? room.placeholder : "Not connected"}
            aria-label="Chat message"
            disabled={!joined || guestBlocked}
            onChange={(e) => {
              setText(e.target.value);
              if (e.target.value.trim()) session?.noteChatTyping();
            }}
            onFocus={() => setUi({ chatFocused: true })}
            onBlur={() => setUi({ chatFocused: false })}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
                toggleChat(false);
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <button type="submit" className="btn btn-primary" disabled={!canSend}>
            Send
          </button>
        </div>
        <div className={`chat-counter${text.length > CHAT_MAX_CHARS ? " over" : ""}`} aria-live="polite">
          {text.length}/{CHAT_MAX_CHARS} · links stay plain text · messages are kept 7 days for safety review · Esc closes
        </div>
      </form>
    </section>
  );
}
